import { Injectable, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ClientService } from '../services/client.service';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { ToastService } from 'src/app/toast/toast.service';
import { AuthService } from '../../../auth/login/services/auth.service';

export interface Client {
  id: number;
  name: string;
  description?: string;
}

export interface CanDeleteClientResponse {
  canDelete: boolean;
  activeUserCount: number;
  message?: string;
}

@Injectable()
export class ClientViewModel {
  packageDialog = false;
  isViewMode = false;
  isEditMode = false;
  clientForm!: FormGroup;
  clientDatas = signal<Client[]>([]);
  clients: any[] = [];
  private originalFormValues: any = null;

  tenantTypes: any[] = [];
  authTypes: any[] = [];
  private originalTenantValues: any[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private http: HttpClient,
    private clientService: ClientService
  ) {
    this.initForm();
  }

  private initForm(): void {
    this.clientForm = this.fb.group({
      id: [null],
      name: [null, Validators.required],
      description: [null, Validators.required],
      tenants: this.fb.array([])
    });
  }

  get tenantsArray(): FormArray {
    return this.clientForm.get('tenants') as FormArray;
  }

  public init(): void {
    this.getAllClients();
    this.loadTenantTypes();
    this.loadAuthTypes();
  }

  private loadTenantTypes(): void {
    this.clientService.getTenantsAll().subscribe({
      next: (response: any) => {
        const tenants = response || [];
        this.tenantTypes = tenants.map((t: any) => ({
          label: t.tenentType,
          value: t.id
        }));
      },
      error: () => { this.tenantTypes = []; }
    });
  }

  private loadAuthTypes(): void {
    this.clientService.getAuthTypes().subscribe({
      next: (response: any) => {
        const values = response || [];
        this.authTypes = values.map((v: any) => ({
          label: v.referenceValue,
          value: v.id
        }));
      },
      error: () => { this.authTypes = []; }
    });
  }

  hideDialog(): void {
    this.packageDialog = false;
    this.isViewMode = false;
    this.isEditMode = false;
    this.originalFormValues = null;
    this.originalTenantValues = [];
    this.clientForm.enable();
    this.clientForm.reset();
    this.tenantsArray.clear();
  }

  openNew(): void {
    this.isEditMode = false;
    this.isViewMode = false;
    this.originalFormValues = null;
    this.originalTenantValues = [];
    this.clientForm.reset();
    this.tenantsArray.clear();
    this.packageDialog = true;
  }

  editClient(client: any): void {
    this.isEditMode = true;
    this.isViewMode = false;

    this.clientForm.patchValue({
      id: client.id,
      name: client.name,
      description: client.description
    });

    this.tenantsArray.clear();

    this.clientService.getClientTenants(client.id).subscribe({
      next: (tenants: any[]) => {
        const activeTenants = (tenants || []).filter((t: any) => t.isActive !== false);
        activeTenants.forEach((t: any) => {
          const authLabel = this.getAuthTypeLabel(t.authType);
          const isBasicAuth = this.isBasicAuthLabel(authLabel);
          const isTokenAuth = this.isTokenAuthLabel(authLabel);
          let authInfoObj: any = {};
          if (t.authInfo) {
            try { authInfoObj = JSON.parse(t.authInfo); } catch (e) {}
          }
          const group = this.fb.group({
            id: [t.id],
            tenantId: [t.tenantId, Validators.required],
            tenantName: [t.tenantName, Validators.required],
            tenantUrl: [t.tenantUrl, Validators.required],
            authType: [t.authType, Validators.required],
            username: [{ value: t.username || '', disabled: false }],
            password: [{ value: t.passwordHash || '', disabled: false }],
            tokenEndpoint: [authInfoObj.tokenEndpoint || ''],
            authorizationEndpoint: [authInfoObj.authorizationEndpoint || ''],
            oauthClientId: [authInfoObj.clientId || ''],
            oauthClientSecret: [authInfoObj.clientSecret || ''],

            isModifiedTenant: [false]
          });
          this.applyAuthValidators(group, t.authType);
          group.valueChanges.subscribe(() => {
            group.get('isModifiedTenant')?.setValue(true, { emitEvent: false });
          });
          this.tenantsArray.push(group);
        });
        this.originalFormValues = this.clientForm.getRawValue();
        this.originalTenantValues = this.tenantsArray.getRawValue();
      },
      error: () => {
        this.originalFormValues = this.clientForm.getRawValue();
        this.originalTenantValues = [];
      }
    });

    this.packageDialog = true;
  }

  viewClient(client: Client): void {
    this.isEditMode = false;
    this.isViewMode = true;

    this.clientForm.patchValue({
      id: (client as any).id,
      name: client.name,
      description: client.description
    });

    this.tenantsArray.clear();

    this.clientService.getClientTenants((client as any).id).subscribe({
      next: (tenants: any[]) => {
        const activeTenants = (tenants || []).filter((t: any) => t.isActive !== false);
        activeTenants.forEach((t: any) => {
          const authLabel = this.getAuthTypeLabel(t.authType);
          let authInfoObj: any = {};
          if (t.authInfo) {
            try { authInfoObj = JSON.parse(t.authInfo); } catch (e) {}
          }
          const group = this.fb.group({
            id: [t.id],
            tenantId: [t.tenantId, Validators.required],
            tenantName: [t.tenantName, Validators.required],
            tenantUrl: [t.tenantUrl, Validators.required],
            authType: [t.authType, Validators.required],
            username: [t.username || ''],
            password: [t.passwordHash || ''],
            tokenEndpoint: [authInfoObj.tokenEndpoint || ''],
            authorizationEndpoint: [authInfoObj.authorizationEndpoint || ''],
            oauthClientId: [authInfoObj.clientId || ''],
            oauthClientSecret: [authInfoObj.clientSecret || ''],
            isModifiedTenant: [false]
          });
          this.tenantsArray.push(group);
        });
        this.clientForm.disable();
      },
      error: () => {
        this.clientForm.disable();
      }
    });

    this.packageDialog = true;
  }

  private getAuthTypeLabel(authTypeId: string): string {
    const found = this.authTypes.find(a => a.value === authTypeId);
    return found ? found.label : '';
  }

  addTenant(): void {
    const group = this.fb.group({
      id: [null],
      tenantId: [null, Validators.required],
      tenantName: [null, Validators.required],
      tenantUrl: [null, Validators.required],
      authType: [null, Validators.required],
      username: [''],
      password: [''],
      tokenEndpoint: [''],
      authorizationEndpoint: [''],
      oauthClientId: [''],
      oauthClientSecret: [''],

      isModifiedTenant: [false]
    });
    this.tenantsArray.push(group);
  }

  removeTenant(index: number): void {
    this.tenantsArray.removeAt(index);
  }

  onAuthTypeChange(index: number): void {
    const group = this.tenantsArray.at(index) as FormGroup;
    const authTypeId = group.get('authType')?.value;
    this.applyAuthValidators(group, authTypeId);
    group.get('username')?.reset('');
    group.get('password')?.reset('');
    group.get('tokenEndpoint')?.reset('');
    group.get('authorizationEndpoint')?.reset('');
    group.get('oauthClientId')?.reset('');
    group.get('oauthClientSecret')?.reset('');
  }

  private isBasicAuthLabel(label: string): boolean {
    return label.toLowerCase().includes('basic');
  }

  private isTokenAuthLabel(label: string): boolean {
    return label.toLowerCase().includes('token');
  }

  private applyAuthValidators(group: FormGroup, authTypeId: string): void {
    const label = this.getAuthTypeLabel(authTypeId);
    const isBasic = this.isBasicAuthLabel(label);
    const isToken = this.isTokenAuthLabel(label);
    const isExisting = !!group.get('id')?.value;

    const usernameCtrl = group.get('username');
    const passwordCtrl = group.get('password');
    const tokenEndCtrl = group.get('tokenEndpoint');
    const authEndCtrl = group.get('authorizationEndpoint');
    const clientIdCtrl = group.get('oauthClientId');
    const clientSecretCtrl = group.get('oauthClientSecret');

    if (isBasic) {
      usernameCtrl?.setValidators(Validators.required);
      passwordCtrl?.setValidators(isExisting ? [] : Validators.required);
      tokenEndCtrl?.clearValidators();
      authEndCtrl?.clearValidators();
      clientIdCtrl?.clearValidators();
      clientSecretCtrl?.clearValidators();
    } else if (isToken) {
      usernameCtrl?.clearValidators();
      passwordCtrl?.clearValidators();
      tokenEndCtrl?.setValidators(Validators.required);
      authEndCtrl?.setValidators(Validators.required);
      clientIdCtrl?.setValidators(Validators.required);
      clientSecretCtrl?.setValidators(isExisting ? [] : Validators.required);
    } else {
      usernameCtrl?.clearValidators();
      passwordCtrl?.clearValidators();
      tokenEndCtrl?.clearValidators();
      authEndCtrl?.clearValidators();
      clientIdCtrl?.clearValidators();
      clientSecretCtrl?.clearValidators();
    }

    [usernameCtrl, passwordCtrl, tokenEndCtrl, authEndCtrl, clientIdCtrl, clientSecretCtrl]
      .forEach(c => c?.updateValueAndValidity({ emitEvent: false }));
  }

  isBasicAuth(index: number): boolean {
    const authTypeId = this.tenantsArray.at(index)?.get('authType')?.value;
    return this.isBasicAuthLabel(this.getAuthTypeLabel(authTypeId));
  }

  isTokenAuth(index: number): boolean {
    const authTypeId = this.tenantsArray.at(index)?.get('authType')?.value;
    return this.isTokenAuthLabel(this.getAuthTypeLabel(authTypeId));
  }

  public getAllClients() {
    this.clientService.getClientsAll().subscribe({
      next: (response: any) => {
        this.clients = response.data || [];
        this.clientDatas.set(response.data || []);
      },
      error: () => {
        this.clientDatas.set([]);
        this.clients = [];
      }
    });
  }

  deleteClient(client: Client): void {
    this.clientService.canDeleteClient(client.id).subscribe({
      next: (response: any) => {
        const result = response.data || response;

        if (result.canDelete) {
          this.confirmationService.confirm({
            message: `Are you sure you want to delete client "${client.name}"?`,
            header: 'Confirm Delete',
            acceptButtonStyleClass: 'p-button-success',
            rejectButtonStyleClass: 'p-button-warn',
            accept: () => {
              this.performDelete(client.id, false, 'Client deleted successfully');
            }
          });
        } else {
          this.confirmationService.confirm({
            message: `Cannot delete the client (${client.name}) as one or more users are still associated with it.<br/><br/>Do you want to proceed with force deletion?`,
            header: 'Warning',
            rejectVisible: true,
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptButtonStyleClass: 'p-button-success',
            rejectButtonStyleClass: 'p-button-warn',
            accept: () => {
              this.performDelete(client.id, true, 'Client and all associated data deleted successfully');
            }
          });
        }
      },
      error: () => {
        this.toastService.error('Failed to check client status. Please try again.');
      }
    });
  }

  private performDelete(clientId: number, force: boolean, successMessage: string): void {
    this.clientService.deleteClient(clientId, force).subscribe({
      next: () => {
        this.toastService.success(successMessage);
        this.getAllClients();
      },
      error: () => {
        this.toastService.error('Failed to delete client');
      }
    });
  }

  private validateTenantUniqueness(): string | null {
    const seen = new Set<string>();
    for (const control of this.tenantsArray.controls) {
      const tenantId = control.get('tenantId')?.value;
      const tenantName = control.get('tenantName')?.value?.trim();
      if (!tenantId || !tenantName) continue;
      const key = `${tenantId}:${tenantName.toLowerCase()}`;
      if (seen.has(key)) {
        const typeName = this.tenantTypes.find(t => t.value === tenantId)?.label || tenantId;
        return `Duplicate combination of Tenant Type "${typeName}" and Tenant Name "${tenantName}" is not allowed within the same client.`;
      }
      seen.add(key);
    }
    return null;
  }

  saveClient(): void {
    this.clientForm.markAllAsTouched();
    this.tenantsArray.controls.forEach(g => (g as FormGroup).markAllAsTouched());

    if (!this.clientForm.get('name')?.valid || !this.clientForm.get('description')?.valid) return;

    if (this.tenantsArray.length === 0) {
      this.toastService.error('At least one tenant configuration is required.');
      return;
    }

    // Validate tenants
    {
      const invalidTenant = this.tenantsArray.controls.find(c => c.invalid);
      if (invalidTenant) {
        this.toastService.error('Please fill all required tenant fields.');
        return;
      }

      const uniqueError = this.validateTenantUniqueness();
      if (uniqueError) {
        this.toastService.error(uniqueError);
        return;
      }
    }

    const clientPayload: any = {
      id: this.clientForm.value.id,
      name: this.clientForm.value.name,
      description: this.clientForm.value.description
    };

    if (this.isEditMode && this.originalFormValues) {
      const isModified =
        this.originalFormValues.name !== this.clientForm.value.name ||
        this.originalFormValues.description !== this.clientForm.value.description;

      if (isModified) {
        clientPayload.isModified = true;
      }
    }

    this.clientService.save(clientPayload as Client).subscribe({
      next: (response: any) => {
        const clientId = response?.data?.id || clientPayload.id;

        if (this.tenantsArray.length > 0 && clientId) {
          this.saveTenants(clientId);
        } else if (this.isEditMode && this.tenantsArray.length === 0 && this.originalTenantValues.length > 0 && clientId) {
          // All tenants removed — send empty array with clientId to deactivate all
          this.clientService.saveClientTenants([{ clientId, _deactivateAll: true }]).subscribe({
            next: () => this.handleSaveSuccess(),
            error: () => {
              this.toastService.success(this.isEditMode ? 'Client updated but tenant removal failed' : 'Client created successfully');
              this.handleSaveSuccess();
            }
          });
        } else {
          this.handleSaveSuccess();
        }
      },
      error: () => this.toastService.error('Client Name already exists')
    });
  }

  private saveTenants(clientId: string): void {
    const tenantPayloads = this.tenantsArray.controls.map(c => {
      const val = (c as FormGroup).getRawValue();
      const authLabel = this.getAuthTypeLabel(val.authType);
      const isBasic = this.isBasicAuthLabel(authLabel);
      const isToken = this.isTokenAuthLabel(authLabel);

      const payload: any = {
        clientId,
        tenantId: val.tenantId,
        tenantName: val.tenantName,
        tenantUrl: val.tenantUrl,
        authType: val.authType,
        isModifiedTenant: val.isModifiedTenant || !val.id
      };

      if (val.id) payload.id = val.id;

      if (isBasic) {
        payload.username = val.username;
        if (val.password) {
          payload.passwordHash = val.password;
        }
        payload.authInfo = null;
      } else if (isToken) {
        payload.username = null;
        payload.passwordHash = null;
        payload.authInfo = JSON.stringify({
          tokenEndpoint: val.tokenEndpoint,
          authorizationEndpoint: val.authorizationEndpoint,
          clientId: val.oauthClientId,
          clientSecret: val.oauthClientSecret || undefined
        });
      } else {
        payload.username = null;
        payload.passwordHash = null;
        payload.authInfo = null;
      }

      return payload;
    });

    this.clientService.saveClientTenants(tenantPayloads).subscribe({
      next: () => this.handleSaveSuccess(),
      error: () => {
        this.toastService.error('Client saved but tenant configuration failed.');
        this.handleSaveSuccess();
      }
    });
  }

  private handleSaveSuccess(): void {
    this.toastService.success(this.isEditMode ? 'Client updated successfully' : 'Client created successfully');
    this.getAllClients();
    this.hideDialog();
  }

  onGlobalFilter(table: any, event: Event): void {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  isSubmitDisabled(): boolean {
    if (!this.clientForm.get('name')?.valid || !this.clientForm.get('description')?.valid) {
      return true;
    }

    if (this.tenantsArray.length === 0) {
      return true;
    }

    if (this.isEditMode && this.originalFormValues) {
      const hasClientChanges =
        this.originalFormValues.name !== this.clientForm.value.name ||
        this.originalFormValues.description !== this.clientForm.value.description;

      const hasTenantChanges = this.tenantsArray.length !== this.originalTenantValues.length ||
        this.tenantsArray.controls.some(c => (c as FormGroup).getRawValue().isModifiedTenant) ||
        this.tenantsArray.controls.some(c => !(c as FormGroup).getRawValue().id);

      return !hasClientChanges && !hasTenantChanges;
    }

    return false;
  }
}
